/**
 * Emergent Google OAuth callback handler.
 *
 * Lifecycle (Issue 14b from the June 2026 audit):
 *   1. AppRouter detects `location.hash.includes('session_id=')` and
 *      mounts this component BEFORE any normal route renders.
 *   2. We synchronously stash the session_id, strip the hash, and call
 *      our backend `/api/auth/google/session` to exchange it for the
 *      standard CaraLoom JWT + refresh-cookie pair.
 *   3. On success we drop the user into their role-appropriate
 *      dashboard. On failure we go back to /auth with a toast.
 *
 * The Emergent OAuth playbook calls out a real race against any global
 * AuthProvider's `/auth/me` check; we use a useRef gate so React 18
 * StrictMode's double-invoke doesn't double-exchange the session_id.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuthFromToken, setMfaSetupFromResponse } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    // Clean the hash up front so a reload doesn't replay the exchange.
    try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch (_e) { /* ignore */ }

    if (!sessionId) {
      setError('We didn’t receive a sign-in code from Google. Please try again.');
      setTimeout(() => navigate('/auth', { replace: true }), 1500);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        // Iter 155 · MFA Enforcement — if backend says "MFA setup
        // required" for this Google account (admin/clinician role),
        // stash the scoped token and drop them onto /mfa-setup
        // instead of a role dashboard.
        if (data?.mfa_setup_required) {
          if (typeof setMfaSetupFromResponse === 'function') {
            setMfaSetupFromResponse(data.token, data.user);
          }
          toast.info('Two-factor authentication is required for your role.');
          navigate('/mfa-setup', { replace: true });
          return;
        }
        if (typeof setAuthFromToken === 'function') {
          setAuthFromToken(data.token, data.user);
        }
        toast.success('Welcome to CaraLoom');
        const route =
          data.user?.role === 'clinician' ? '/clinician/dashboard'
          : data.user?.role === 'agency' ? '/agency/dashboard'
          : data.user?.role === 'discharge_planner' ? '/discharge-planner/dashboard'
          : data.user?.role === 'payer' ? '/payer/dashboard'
          : data.user?.role === 'admin' ? '/admin/dashboard'
          : '/patient/dashboard';
        navigate(route, { replace: true, state: { user: data.user } });
      } catch (e) {
        // Iter 153 · pydantic 422 responses come back as an array
        // of {loc, msg, type} objects. Rendering that as a React
        // child throws "Objects are not valid as a React child".
        // Normalise every shape to a plain string.
        const raw = e?.response?.data?.detail;
        let detail;
        if (typeof raw === 'string') {
          detail = raw;
        } else if (Array.isArray(raw)) {
          detail = raw.map((r) =>
            typeof r === 'string' ? r : (r?.msg || JSON.stringify(r))
          ).join('; ');
        } else if (raw && typeof raw === 'object') {
          detail = raw.msg || JSON.stringify(raw);
        } else {
          detail = 'Could not complete Google sign-in. Please try again.';
        }
        setError(detail);
        toast.error(detail);
        setTimeout(() => navigate('/auth', { replace: true }), 2000);
      }
    })();
  }, [setAuthFromToken, setMfaSetupFromResponse, navigate]);

  return (
    <div className="min-h-screen bg-cn-bg flex items-center justify-center p-6" data-testid="auth-callback">
      <div className="cn-card max-w-sm w-full p-8 text-center">
        <CaraLoomLogo />
        <div className="mt-6 flex items-center justify-center">
          <Loader2 className="w-7 h-7 text-cn-primary animate-spin" />
        </div>
        <p className="font-display text-lg font-semibold text-cn-text mt-4">
          {error ? 'Sign-in failed' : 'Signing you in…'}
        </p>
        <p className="text-sm text-cn-text-muted mt-2">
          {error || 'Verifying your Google sign-in and unlocking your CaraLoom.'}
        </p>
      </div>
    </div>
  );
}
